/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any */

import { wrap } from 'comlink';

import { container } from '@/container';
import { UserController } from '@/controllers/UserController';
import CONSTANTS from '@/helpers/constants';
import { TripNotesManager } from '@/managers/TripNotesManager';
import { Logger } from '@/services/Logger';
import { TripUpdateReq } from '@/services/signalr/MessageTypes/TripUpdateReq';
import { Signalr } from '@/services/signalr/Signalr';
import { plainObject } from '@/utils/plainObject';

const logger = container.get(Logger);
const userController = container.get(UserController);

/**
 * Load the web worker for message construction.
 * @type {{
 *   createTripMessage: (function(trip: object, options?: {hasPatientFields: boolean, hasPatientEdit: boolean, hasCallSources: boolean}): Promise<object>),
 * }}
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const worker: any = wrap(new Worker(new URL('../../store/events/updateTrip.worker.js', import.meta.url), { type: 'module' }));

export interface CreateTripParams {
  trip: Record<string, unknown>;
  contextKey?: string;
  unitId?: string;
  returnTrip?: boolean;
  returnPickupTime?: string;
  returnWillCall?: boolean;
}

/**
 * Create a new trip via SignalR
 */
export async function createTrip(params: CreateTripParams): Promise<unknown> {
  let response;
  const startMs = performance.now();
  try {
    const signalr = await container.getAsync(Signalr);
    // eslint-disable-next-line no-param-reassign
    params.trip.Who = userController.activeUser.fullName;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    response = await signalr.invoke(TripUpdateReq, {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      ...await worker.createTripMessage(plainObject(params.trip)),
      contextKey: params.contextKey ?? CONSTANTS.UI.CONTEXTS.CALL_TAKING,
      ...(params.unitId ? { unitId: params.unitId } : {}),
    });

    logger.track('trip.create', {
      durationMs: Math.round(performance.now() - startMs),
      hadUnit: !!params.unitId,
      hadReturnTrip: !!params.trip.returnTrip,
      contextKey: params.contextKey ?? CONSTANTS.UI.CONTEXTS.CALL_TAKING,
      success: true,
    });

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (params.trip.returnTrip) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      params.trip.id = response.id;
      // Dynamic import and call createReturnTrip for return trip handling
      const { createReturnTrip: createReturnTripFn } = await import('./createReturnTrip');
      await createReturnTripFn({
        trip: params.trip,
      });
    }

    const tripNotesManager = await container.getAsync(TripNotesManager);
    tripNotesManager.clearTmpTripNotes();
    return response;
  }
  catch (error) {
    logger.track('trip.create', {
      durationMs: Math.round(performance.now() - startMs),
      hadUnit: !!params.unitId,
      hadReturnTrip: !!params.trip.returnTrip,
      contextKey: params.contextKey ?? CONSTANTS.UI.CONTEXTS.CALL_TAKING,
      success: false,
      errorType: (error as Error).name,
    });
    logger.captureException(error as Error, { tags: { createTrip: 'createTrip' } });
    throw error;
  }
}
